import LayoutUtils from 'src/utils/layout_utils';
import { updateAvailableHeadingMeta, updateLockboxHeadingMeta } from './edit_form_time_section_heading';
import { createSubmitStateUpdater } from './edit_form_time_section_submit_state';
import { initAvailableSlots } from './edit_form_time_section_available';
import {
  createLockboxHandlers,
  initLockboxListeners
} from './edit_form_time_section_lockbox';

/**
 * Time section inside the edit form: available slots vs lockbox, day toggles, slot selection, lockbox date/time.
 * Used by edit_form.js when initializing the edit form and after reloading dependencies (when time_form_html is replaced).
 */
export function initEditTimeSection(form) {
  const section = form.querySelector('.js-showing-view-time-section');
  if (!section) {
    return;
  }

  const elements = {
    showtimeTypeSelect: form.querySelector('.js-showing-view-showtime-type'),
    availableWrap: section.querySelector('.js-showing-view-time-available'),
    customWrap: section.querySelector('.js-showing-view-time-custom'),
    lockboxWrap: section.querySelector('.js-showing-view-time-lockbox'),
    headingTitle: section.querySelector('.js-showing-view-time-heading-title'),
    selectedInput: form.querySelector('.js-showing-view-time-selected'),
    lockboxShowtimeInput: form.querySelector('.js-showing-view-lockbox-showtime'),
    lockboxDateSelect: section.querySelector('.js-showing-view-lockbox-date'),
    lockboxTimeSelect: section.querySelector('.js-showing-view-lockbox-time'),
    customDateInput: section.querySelector('.js-showing-view-custom-showtime-date'),
    customTimeInput: section.querySelector('.js-showing-view-custom-showtime-time'),
    customDurationInput: section.querySelector('.js-showing-view-custom-showtime-duration'),
    submitText: form.querySelector('.js-showing-view-edit-submit-text'),
    submitInfo: form.querySelector('.js-showing-view-edit-submit-info')
  };

  const originalValues = {
    originalShowtimeValue: form.dataset.originalShowtimeValue || '',
    originalLockboxShowtimeValue: form.dataset.originalLockboxShowtimeValue || '',
    originalCustomDateValue: elements.customDateInput?.defaultValue || '',
    originalCustomTimeValue: elements.customTimeInput?.defaultValue || '',
    originalCustomDurationValue: elements.customDurationInput?.defaultValue || ''
  };

  const updateSubmitInfo = createSubmitStateUpdater(form, section, elements, originalValues);

  const updateHeadingMeta = (dayKey) =>
    updateAvailableHeadingMeta(section, elements.headingTitle, dayKey);

  const updateAvailableHeadingMetaBound = () => {
    const activeDay = section.querySelector('.js-showing-view-time-day-toggle--active')?.dataset.dayKey;
    if (activeDay) {
      updateHeadingMeta(activeDay);
      return;
    }

    if (elements.headingTitle) {
      elements.headingTitle.textContent = elements.headingTitle.dataset.defaultText || 'No dates available.';
    }
  };

  const updateLockboxHeadingMetaBound = () =>
    updateLockboxHeadingMeta(
      section,
      elements.headingTitle,
      elements.lockboxWrap,
      elements.lockboxDateSelect
    );

  const syncMode = () => {
    const mode = elements.showtimeTypeSelect?.value;
    const customMode = mode === 'custom';
    const lockboxMode = mode === 'lockbox';

    LayoutUtils.toggle(elements.availableWrap, !customMode && !lockboxMode);
    LayoutUtils.toggle(elements.customWrap, customMode);
    LayoutUtils.toggle(elements.lockboxWrap, lockboxMode);
    if ((customMode || lockboxMode) && elements.selectedInput) {
      elements.selectedInput.value = '';
    }
    if (!lockboxMode && elements.lockboxShowtimeInput) {
      elements.lockboxShowtimeInput.value = '';
    }
    if (lockboxMode) {
      updateLockboxHeadingMetaBound();
    } else if (customMode && elements.headingTitle) {
      elements.headingTitle.textContent = 'Custom Showtime';
    } else {
      updateAvailableHeadingMetaBound();
    }
    updateSubmitInfo();
  };

  const lockboxCallbacks = {
    updateLockboxHeadingMeta: updateLockboxHeadingMetaBound,
    updateSubmitInfo
  };
  const lockboxHandlers = createLockboxHandlers(section, elements, lockboxCallbacks);

  initAvailableSlots(section, elements, { updateSubmitInfo });
  initLockboxListeners(section, elements, lockboxHandlers);
  [
    elements.customDateInput,
    elements.customTimeInput,
    elements.customDurationInput
  ].forEach((input) => {
    if (!input || input.dataset.bound === 'true') return;
    input.dataset.bound = 'true';
    input.addEventListener('input', updateSubmitInfo);
    input.addEventListener('change', updateSubmitInfo);
  });

  lockboxHandlers.updateLockboxTimeOptions();
  lockboxHandlers.syncLockboxHiddenValue();
  syncMode();
  updateSubmitInfo();
}
